import type { LetterCharacter } from '../types';
import type { Letter } from './Letter';
import { createLetter } from './Letter';
import { SCRABBLE_DISTRIBUTION } from '../constants/letterDistribution';
import { LETTER_POOL_SIZE } from '../constants/gameConstants';

export interface LetterCount {
  [key: string]: number;
}

export interface LetterPool {
  availableLetters: LetterCount; // Remaining letters by type
  totalRemaining: number; // Sum of all available letters
  initialDistribution: LetterCount; // Starting distribution (immutable)
  isExhausted: boolean; // True when totalRemaining === 0
}

/**
 * Create a new letter pool with scaled distribution
 */
export function createLetterPool(): LetterPool {
  // Scale Scrabble distribution (100 base) to LETTER_POOL_SIZE
  const scaleFactor = LETTER_POOL_SIZE / 100;
  const initialDistribution: LetterCount = {};
  let totalRemaining = 0;

  // First pass: round all counts
  for (const [char, count] of Object.entries(SCRABBLE_DISTRIBUTION)) {
    const scaledCount = Math.round(count * scaleFactor);
    initialDistribution[char] = scaledCount;
    totalRemaining += scaledCount;
  }

  // Adjust for rounding errors to hit exact LETTER_POOL_SIZE
  const difference = LETTER_POOL_SIZE - totalRemaining;
  if (difference !== 0) {
    // Add or subtract from the most common letter (E)
    initialDistribution['E'] += difference;
    totalRemaining = LETTER_POOL_SIZE;
  }

  return {
    availableLetters: { ...initialDistribution },
    totalRemaining,
    initialDistribution,
    isExhausted: false,
  };
}

/**
 * Draw random letters from the pool
 */
export function drawRandomLetters(pool: LetterPool, count: number, maxPerLetter?: number): { letters: Letter[]; updatedPool: LetterPool } {
  const letters: Letter[] = [];
  const updatedAvailable = { ...pool.availableLetters };
  let remaining = pool.totalRemaining;
  const drawnCounts: Record<string, number> = {};

  for (let i = 0; i < count && remaining > 0; i++) {
    // Build weighted array of available letters, respecting per-letter cap
    const weightedLetters: LetterCharacter[] = [];
    for (const [char, charCount] of Object.entries(updatedAvailable)) {
      if (charCount <= 0) continue;
      if (maxPerLetter !== undefined && (drawnCounts[char] ?? 0) >= maxPerLetter) continue;
      for (let j = 0; j < charCount; j++) {
        weightedLetters.push(char as LetterCharacter);
      }
    }

    if (weightedLetters.length === 0) break;

    // Pick random letter
    const randomIndex = Math.floor(Math.random() * weightedLetters.length);
    const selectedChar = weightedLetters[randomIndex];

    // Create letter and update pool
    letters.push(createLetter(selectedChar, 'pool'));
    updatedAvailable[selectedChar]--;
    remaining--;
    drawnCounts[selectedChar] = (drawnCounts[selectedChar] ?? 0) + 1;
  }

  return {
    letters,
    updatedPool: {
      ...pool,
      availableLetters: updatedAvailable,
      totalRemaining: remaining,
      isExhausted: remaining === 0,
    },
  };
}

/**
 * Return letters to the pool
 */
export function returnLetters(pool: LetterPool, letters: Letter[]): LetterPool {
  const updatedAvailable = { ...pool.availableLetters };
  let remaining = pool.totalRemaining;

  for (const letter of letters) {
    updatedAvailable[letter.character]++;
    remaining++;
  }

  return {
    ...pool,
    availableLetters: updatedAvailable,
    totalRemaining: remaining,
    isExhausted: false,
  };
}
